<template>
    <div class="flex flex-col gap-4 h-full">
        <div class="flex justify-between">
            <div class="flex gap-2">
                <h1 :class="{'text-white/50 underline': organization, 'cursor-pointer': organization}" @click="() => handleReset()">{{ $t("sessions", {count: 2}) }}</h1>
                <h1 v-if="organization"> 
                    {{ '>' }}
                    {{ organization }}
                </h1>
            </div>
            <BaseButton 
                v-if="messageList"
                :variant="isCopied ? undefined : 'primary'"
                :outline="isCopied"
                style="width: fit-content;"
                @click="() => copySession()"
            >
                {{ isCopied ? $t("copied") : $t("share") }}
            </BaseButton>
        </div>
        <GridRow v-if="!organization">
            <SessionOrgCard :span-md="6" v-for="organization in organizationList" :key="organization.id" :organization @click="() => router.replace({path: route.path, query: {organization: organization.slug}})"/>
        </GridRow>
        <div v-else-if="errorMessage" class="flex flex-1 w-full h-full items-center justify-center">
            <BaseBoxColor class="flex gap-2" variant="danger">
                <h4 class="text-danger">Error: </h4>
                <h4 class="text-white/75">{{ errorMessage }}</h4>
            </BaseBoxColor>
        </div>
        <GridRow v-else :cols="5">
            <GridCol :span="2">
                <SessionFilters 
                    ref="sessionFiltersRef"
                    v-model:filters="sessionFilters"
                    :channel-type-options="channelTypeOptions"
                    @apply-filters="applyFilters"
                />
                <div class="overflow-y-auto 2xl:h-[50vh] h-[39vh]">
                    <div v-if="skeletonLoading" class="flex flex-col gap-4 pt-4">
                        <Skeletor class="rounded-lg border border-white/10" width="100%" height="200px"/>
                        <Skeletor class="rounded-lg border border-white/10" width="100%" height="200px"/>
                    </div>
                    <div class="flex flex-col gap-4 pt-4" v-else>
                        <SessionCard
                            v-if="sessionList.length"
                            v-for="session in sessionList"
                            :session
                            :selected="selectedSession == session.sessionId"
                            @click="selectedSession = session.sessionId"
                        />
                        <div v-else class="w-full flex justify-center items-center mt-4">
                            <h6 class="text-white/75">{{ $t("sessionMissingMessage") }}</h6>
                        </div>
                    </div>
                </div>
                <div v-if="sessionList.length" class="w-full flex justify-center mt-3">
                    <VueAwesomePaginate v-model="actualPage" :total-items="totalSessions" :items-per-page="5" :max-pages-shown="3" @click="handlePageChange"/>
                </div>
            </GridCol>
            <GridCol :span="3">
                <BaseBoxColor id="chatBox" variant="dark" class="w-full overflow-y-auto 2xl:h-[74vh] h-[68vh]" :class="{'flex justify-center items-center': !messageList}">
                    <ChatMessagesList v-if="messageList" :chats="messageList" class="py-3 pt-0" />
                    <h6 v-else class="text-white/75">{{ $t("sessionSelectionMessage") }}</h6>
                </BaseBoxColor>
            </GridCol>
        </GridRow>
    </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useOrganizationStore from "@/stores/organization";
import { SessionService } from "@/api/services";
import ChatMessagesList from "../components/chat/ChatMessagesList.vue";
import { DateTime } from "luxon";
import { VueAwesomePaginate } from "vue-awesome-paginate";
import SessionOrgCard from "@/components/sessions/SessionOrgCard.vue"
import SessionCard from "@/components/sessions/SessionCard.vue"
import SessionFilters from "@/components/sessions/SessionFilters.vue"
import { useI18n } from "vue-i18n";
import { Skeletor } from "vue-skeletor";

const { t } = useI18n();
const { fetchOrganizationList } = useOrganizationStore();
const { organizationList } = storeToRefs(useOrganizationStore());

const sessionFiltersRef = ref(null)
const skeletonLoading = ref(false)
const isLoading = ref(false)

const route = useRoute()
const router = useRouter()

const sessionFilters = ref({
    channel: [],
    sessionId: '',
    message: '',
    date: ''
})
const sessionList = ref([])
const selectedSession = ref()
const isCopied = ref(false) 
const actualPage = ref(1)
const totalSessions = ref(0)

const errorMessage = ref('')
const channelTypeOptions = ["mail", "chat", "telegram"]

/* --- COMPUTED VARIABLES --- */
const organization = computed(() => route.query.organization)
const messageList = computed(() => {
    let session = sessionList.value.find((el) => el.sessionId == selectedSession.value)
    if(session) return session.history.map((message) => {
        let formattedObj = {
            id: message._id,
            user: undefined,
            message: message.sourceType == "channel" ? message.question : message.response,
            time: getTimeFromDate(message.time),
            dim: message.sourceType == "DIM" ? message.source : undefined
        }
        if(message.sourceType == "channel"){
            formattedObj.user = { name: session.user?.username && t("anonymous") } 
        }

        return formattedObj
    })
})

/* --- FUNCTIONS --- */
const applyFilters = () => {
    let query = {}
    let stringArray = JSON.stringify(sessionFilters.value.channel)
    query.organization = organization.value
    if(sessionFilters.value.date[0]){
        query.start_date = sessionFilters.value.date[0]
        if(!sessionFilters.value.date[1]){
            query.end_date = sessionFilters.value.date[0]
        } else {
            query.end_date = sessionFilters.value.date[1]
        }
    }
    if(sessionFilters.value.channel.length) query.channel = stringArray
    if(sessionFilters.value.sessionId) query.sessionId = sessionFilters.value.sessionId
    if(sessionFilters.value.search) query.search = sessionFilters.value.search

    router.push({
        name: route.name,
        query
    })
}

const fetchSessions = async () => {
    try{
        skeletonLoading.value = true
        let date = []
        if(route.query.page) actualPage.value = Number(route.query.page)
        if(route.query.sessionId) sessionFiltersRef.value?.setSessionSearch(route.query.sessionId)
        if(route.query.search) sessionFiltersRef.value?.setMessageSearch(route.query.search)
        if(route.query.channel) sessionFilters.value.channel = JSON.parse(route.query.channel)
        if(route.query.start_date) {
            date.push(route.query.start_date)
            date.push(route.query.end_date ? route.query.end_date : route.query.start_date)
            sessionFiltersRef.value?.setDatePicker(date)
        }

        let sessionsResponse = await SessionService.read(window.location.search)
        sessionList.value = sessionsResponse.sessionList.results
        if(sessionList.value.length == 1){
            selectedSession.value = sessionList.value[0].sessionId
        }
        totalSessions.value = sessionsResponse.sessionList.total
        errorMessage.value = ''
    } catch (e){
        sessionList.value = []
        errorMessage.value = e
        console.error(e)
    } finally {
        skeletonLoading.value = false
    }

}

/* --- HELPERS --- */
const copySession = () => {
    navigator.clipboard.writeText(`${window.location.origin}${route.path}?organization=${organization.value}&sessionId=${selectedSession.value}`)
    isCopied.value = true
    setTimeout(() => {
        isCopied.value = false;
    }, 2000)
}

const handlePageChange = (newPage) => {
    actualPage.value = newPage
    router.push({
        name: route.name,
        query: {...route.query, page: actualPage.value}
    })
}

const getTimeFromDate = (date) => {
    return DateTime
        .fromISO(date)
        .toFormat('dd/MM/yyyy HH:mm');
}

const handleReset = () => {
    if(organization.value) router.replace({ 
        path: route.path,
        query: {} 
    })
}

/* --- HOOKS --- */
onMounted(async () => {
    try{
        isLoading.value = true
        await fetchOrganizationList();
        if(route.query.organization) {
            await fetchSessions()
        }
    } catch (e) {
        console.error(e)
    } finally {
        isLoading.value = false
    }
})

/* --- WATCH --- */
watch(() => route.query, async () => {
    if(organization.value) await fetchSessions()
}, { immediate: false, deep: true})

watch(() => messageList.value, async () => {
    const el = document.querySelector('#chatBox')
    await nextTick()
    if (el) {
        el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth'
        });
    }
}, {deep: true})

</script>

<style lang="postcss">
    .hover-selectable{
        cursor: pointer;
        transition: border 150ms;
    }
    .hover-selectable:hover{
        border: #00FFAF 1px solid
    }

    .pagination-container {
        display: flex;
        column-gap: 10px;
    }

    .paginate-buttons {
        height: 30px;
        padding-right: 12px;
        padding-left: 12px;
        border-radius: 12px;
        cursor: pointer;
        border: #00FFAF 1px solid;
        color: #00FFAF 
    }

    .paginate-buttons:hover {
        background-color: #00ffae6e;
        color: black
    }

    .active-page {
        background-color: #00FFAF;
        color: black;
    }

    .active-page:hover {
        background-color: #00ffae6e;
    }

    /* Shimmer */
    .vue-skeletor:not(.vue-skeletor--shimmerless):after {
        /* 
        Change the shimmer color, its a simple 90 deg 
        linear horizontal gradient, adjust it however
        you like.
        */
        background: linear-gradient(
        90deg, 
        rgba(255,255,255,0) 0%, 
        rgba(255, 255, 255, 0.541) 50%, 
        rgba(255,255,255,0) 100%
        );
    
        /* Change any css keyframes animation property */
        animation-duration: 1s; 
        animation-timing-function: ease-in-out;
        /* ... */
    
        /* 
        Or implement your custom shimmer css animation 
        if you want it's pure css no magic happening =)
        */
    }
    
    /* Default keyframes used in skeletor */
    @keyframes shimmer {
        100% {
        transform: translateX(100%);
        }
    }
</style>